// ─── ErrorBoundary ─────────────────────────────────────────────────────────
//
// A simple class-component error boundary. Wraps high-risk subtrees (the
// inbox is the obvious one — heavy state, sockets, Virtuoso, dozens of
// branches) so a render-time exception doesn't blank the whole SPA.
//
// Why class component: React still requires class for `componentDidCatch` /
// `getDerivedStateFromError`. There is no hook-based equivalent as of React
// 19. Keep this file ES5-classy.
//
// On caught error we log to console (so devs see it in DevTools) and render
// a small recovery card with a "Reload" button. Production telemetry hooks
// can plug into `componentDidCatch` later if/when we wire a real reporter.

import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label || 'unnamed', error, info);
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch (_) { /* swallow */ }
    }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback(this.state.error, this.reset);
      }
      return (
        <div className="error-boundary-card" role="alert">
          <h5>Something went wrong</h5>
          <p>
            The {this.props.label || 'page'} hit an unexpected error.
            Refreshing usually resolves it.
          </p>
          <details>
            <summary>Error detail</summary>
            <pre>{String(this.state.error?.message || this.state.error)}</pre>
          </details>
          <div className="error-boundary-actions">
            <button type="button" onClick={this.reset}>Try again</button>
            <button type="button" onClick={() => window.location.reload()}>Reload page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
